<template>
  <div class="efficiency-card">
    <div class="module-title">灌溉效率评估</div>
    <div class="efficiency-ring">
      <div ref="ringChartRef" class="ring-chart"></div>
    </div>
    <div class="metric-list">
      <div v-for="item in metrics" :key="item.name" class="metric-item">
        <span>{{ item.name }}</span>
        <strong>{{ item.value }}</strong>
        <strong>{{ item.value }}</strong>
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import * as echarts from "echarts";

const metrics = [
  { name: "均匀度", value: "87%" },
  { name: "节水率", value: "18%" },
  { name: "能耗下降", value: "12%" },
];

const ringChartRef = ref(null);
let chartInstance = null;

const getRingOption = () => ({
  backgroundColor: "transparent",
  title: {
    text: "灌溉效率\n92%",
    left: "center",
    top: "center",
      textStyle: {
        color: "#ffffff",
      fontSize: 30,
      fontWeight: "bold",
      lineHeight: 46,
      textShadowColor: "rgba(97, 255, 243, 0.45)",
      textShadowBlur: 10,
    },
  },
  graphic: [
    {
      type: "circle",
      left: "center",
      top: "center",
      shape: {
        cx: 0,
        cy: 0,
        r: 84,
      },
      style: {
        fill: "rgba(5, 58, 55, 0.95)",
        stroke: "rgba(100, 255, 244, 0.44)",
        lineWidth: 3,
        shadowColor: "rgba(75, 255, 239, 0.28)",
        shadowBlur: 16,
      },
      z: 0,
    },
    {
      type: "circle",
      left: "center",
      top: "center",
      shape: {
        cx: 0,
        cy: 0,
        r: 62,
      },
      style: {
        fill: "rgba(0, 35, 35, 0.75)",
        stroke: "rgba(83, 255, 241, 0.18)",
        lineWidth: 2,
      },
      z: 1,
    },
  ],
  series: [
    {
      type: "pie",
      radius: ["86%", "90%"],
      center: ["50%", "50%"],
      startAngle: 115,
      silent: true,
      label: { show: false },
      labelLine: { show: false },
      data: [
        {
          value: 74,
          itemStyle: {
            color: "#4ffff0",
            borderRadius: 12,
            shadowColor: "rgba(61, 255, 240, 0.48)",
            shadowBlur: 10,
          },
        },
        { value: 26, itemStyle: { color: "transparent" } },
      ],
    },
    {
      type: "pie",
      radius: ["72%", "84%"],
      center: ["50%", "50%"],
      startAngle: 88,
      silent: true,
      label: { show: false },
      labelLine: { show: false },
      data: [
        {
          value: 92,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
              { offset: 0, color: "#69fff4" },
              { offset: 0.48, color: "#2fe8dd" },
              { offset: 1, color: "#0fa69d" },
            ]),
            borderRadius: 18,
            shadowColor: "rgba(72, 255, 242, 0.45)",
            shadowBlur: 14,
          },
        },
        { value: 8, itemStyle: { color: "rgba(52, 255, 239, 0.13)" } },
      ],
    },
    {
      type: "pie",
      radius: ["58%", "61%"],
      center: ["50%", "50%"],
      startAngle: 95,
      silent: true,
      label: { show: false },
      labelLine: { show: false },
      data: [
        { value: 82, itemStyle: { color: "rgba(80, 255, 243, 0.66)", borderRadius: 8 } },
        { value: 18, itemStyle: { color: "rgba(80, 255, 243, 0.1)" } },
      ],
    },
  ],
});

const initChart = () => {
  if (!ringChartRef.value) return;
  chartInstance = echarts.init(ringChartRef.value);
  chartInstance.setOption(getRingOption());
};

const resizeChart = () => {
  if (chartInstance) chartInstance.resize();
};

onMounted(() => {
  nextTick(initChart);
  window.addEventListener("resize", resizeChart);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
});
</script>

<style lang="less" scoped>
.efficiency-card {
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0 0 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.module-title {
  position: relative;
  height: 68px;
  line-height: 68px;
  padding-left: 18px;
  color: #ffffff;
  font-size: 26px;
  font-weight: bold;
  text-align: left;
  margin-bottom: 24px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 20px;
    width: 5px;
    height: 28px;
    background: #39fff0;
    box-shadow: 0 0 12px rgba(57, 255, 240, 0.8);
  }
}

.efficiency-ring {
  height: 336px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.ring-chart {
  width: 318px;
  height: 318px;
}

.metric-list {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 14px;
  height: auto;
  margin: 30px 0;
}

.metric-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
  background: linear-gradient(180deg, rgba(7, 83, 78, 0.72), rgba(1, 54, 54, 0.88));
  border: 1px solid rgba(132, 255, 245, 0.78);
  border-radius: 6px;
  box-shadow: inset 0 0 20px rgba(92, 255, 238, 0.24),
    0 0 14px rgba(69, 255, 241, 0.14);

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 18px;
    height: 18px;
    border-color: #6efff5;
    border-style: solid;
  }

  &::before {
    left: -1px;
    top: -1px;
    border-width: 2px 0 0 2px;
  }

  &::after {
    right: -1px;
    bottom: -1px;
    border-width: 0 2px 2px 0;
  }

  span {
    font-size: 25px;
    line-height: 1;
  }

  strong {
    margin-top: 20px;
    color: #ffffff;
    font-size: 30px;
    line-height: 1;
  }
}
</style>
